import { compareStrings, removeDuplicates } from "../../../../utils";
import apiService from "@/../api/rest";
import DisabledItemsModal from "@/components/modal/disabledItemsModal/DisabledItemsModal";
import { isEqual } from "lodash";

export const INVALIDATE_VENDOR_ITEMS_CACHE_AFTER_MINUTES = 15;

/**
 * Filter items by matching day parts
 *
 * @param {Item} item
 * @param {String[]} actualDayParts
 */
export const isItemAvailableDayParts = (item, actualDayParts) => {
  const itemDayParts = item?.itemAvailability?.dayParts || [];
  const activeDayPartIds = actualDayParts?.map((actualDayPart) => actualDayPart.id?.toString()) || [];
  return !itemDayParts.length || itemDayParts.some((dayPartId) => activeDayPartIds.includes(dayPartId?.toString()));
};

/**
 * Checks cart item modifiers against current modifier list in state,
 * Returns false if modifiers structure or cost have changed
 *
 * Creates 2 objects with the current modifiers structure and cost then compares both objects
 * using isEqual
 *
 * @param {object} cartItem: current item from cart being revised
 * @param {object} menuItem: current item from the menu
 * @returns {boolean}
 */
export const isModifierValid = (cartItem, menuItem) => {
  try {
    const createModsCostTree = (modifierGroups) => {
      if (!modifierGroups) return [];
      return modifierGroups?.map((modifierGroup) => ({
        modifiers: modifierGroup.modifiers?.map((modifier) => ({
          cost: modifier.cost,
          modifierGroups: createModsCostTree(modifier?.modifierGroups)
        }))
      }));
    };

    const cartItemCostTree = createModsCostTree(cartItem.item.modifierGroups);
    const menuItemCostTree = createModsCostTree(menuItem.modifierGroups);

    return isEqual(cartItemCostTree, menuItemCostTree);
  } catch (e) {
    return false;
  }
};

/**
 * Returns if an item needs to be disabled from cart or not based on the cost status and modifiers
 *
 * @export
 * @param {object} cartItem: current item from cart being revised
 * @param {object} menuItem: current item from the menu
 * @returns {boolean}
 */
export const shouldItemBeDisabled = (cartItem, menuItem, dayParts = []) => {
  // if menu item doesn't exist in current menu items add to disabled items, this will work for alcohol items too.
  if (!menuItem) return true;
  return (
    menuItem.soldOut ||
    !menuItem.isActive ||
    menuItem.cost !== cartItem.item.cost ||
    !isModifierValid(cartItem, menuItem) ||
    !isItemAvailableDayParts(menuItem, dayParts)
  );
};

export const excludeAlcoholItemsIfRequired = (item, alcoholEnabled) => {
  return alcoholEnabled ? true : !item.isAlcohol;
};

export const sortPosCategories = (menu) => {
  return menu.sort((categoryA, categoryB) => {
    const sortOrderOfA = categoryA.sortOrder == 0 ? Infinity : categoryA.sortOrder;
    const sortOrderOfB = categoryB.sortOrder == 0 ? Infinity : categoryB.sortOrder;

    return sortOrderOfA - sortOrderOfB || compareStrings(categoryA.displayName, categoryB.displayName, true);
  });
};

export const sortItems = (items) => {
  return items.sort((itemA, itemB) => {
    const sortOrderOfA = itemA.sortOrder === "" ? Infinity : itemA.sortOrder;
    const sortOrderOfB = itemB.sortOrder === "" ? Infinity : itemB.sortOrder;

    return sortOrderOfA - sortOrderOfB || compareStrings(itemA.name, itemB.name, true);
  });
};

export const filterItems = (items, isAlcoholEnabled, activeDayParts) => {
  return items
    .filter((item) => !item.soldOut)
    .filter((item) => excludeAlcoholItemsIfRequired(item, isAlcoholEnabled))
    .filter((item) => isItemAvailableDayParts(item, activeDayParts));
};

export const getDefaultState = () => {
  return {
    cachedDateTime: null,
    vendorMenu: [],
    modifiers: null,
    parsedModifiersByUUID: {}
  };
};

/**
 * This function takes the menu structured as the node layer sends and
 * maps it to a flat array of sorted items.
 *
 * @param {PosCategories[]} menu
 * @return {Item[]}
 */
export const mapMenuToItems = (menu) => {
  return sortItems(
    menu
      .map((posCategory) => {
        return [
          ...posCategory.items,
          ...posCategory.posSubCategories
            .map((posSubCategory) => {
              return posSubCategory.items;
            })
            .flat()
        ];
      })
      .flat()
  );
};

// initial state
const state = getDefaultState();

const getters = {
  getModifiersByUuid(state) {
    return (uuid) => state.modifiers[uuid];
  },
  getAllItems: (state, getters) => {
    return mapMenuToItems(getters.getVendorMenu);
  },
  getItemByID: (state, getters) => (id) => {
    return getters.getAllItems.find((item) => {
      return item?.id === id;
    });
  },
  /**
   * This method returns an item with the given sku if it exist in the vendor items
   * Note: unlike the getters getItemByID and getAllItems, getItemBySku doesn't
   * apply the filters applied in getVendorMenu and it returns Alcohol items
   * even if the alcohol is disabled for the vendor.
   *
   * @param {string} sku
   * @return {Item}
   */
  getItemBySku: (state) => (sku) => {
    const items = mapMenuToItems(state.vendorMenu);
    return items.find((item) => {
      return item?.sku === sku;
    });
  },
  getVendorMenu: (state, getters, rootState, rootGetters) => {
    const isAlcoholEnabled = getters.isAlcoholEnabled;
    const activeDayParts = rootGetters["dayParts/getActiveDayParts"];

    return state.vendorMenu
      .map((posCategory) => ({
        ...posCategory,
        items: filterItems(posCategory.items, isAlcoholEnabled, activeDayParts),
        posSubCategories: posCategory.posSubCategories.map((posSubCategory) => ({
          ...posSubCategory,
          items: filterItems(posSubCategory.items, isAlcoholEnabled, activeDayParts)
        }))
      }))
      .filter(
        (category) => category.items.length || category.posSubCategories.filter((subCategory) => subCategory.items.length).length
      );
  }
};

// actions
const actions = {
  setActiveDayPart({ dispatch }) {
    dispatch("dayParts/setVenueDayParts", null, { root: true });
  },
  fetchVendorMenu({ commit, state, getters, dispatch, rootGetters }, vendorId) {
    const vendor = rootGetters["vendor/getVendorByID"](vendorId);

    // Caching active vendor items
    const activeVendorId = getters.getActiveVendor && getters.getActiveVendor.id;
    const cacheIsValid = (new Date() - state.cachedDateTime) / 60000 < INVALIDATE_VENDOR_ITEMS_CACHE_AFTER_MINUTES;
    const venueId = rootGetters["venue/getActiveVenue"];

    const orderAheadVendorId = rootGetters["orderAhead/getActiveVendorId"];
    //Will only reset selected pickup when there's already data in th order ahead vendor
    //This will also prevent setSelectedPickup being called in the MAPI flow
    if (orderAheadVendorId && orderAheadVendorId !== vendor.id) {
      dispatch("orderAhead/setSelectedPickup", { time: null, id: null }, { root: true });
    }

    if (activeVendorId === vendor.id && cacheIsValid) {
      return state.vendorMenu;
    }

    commit("setActiveVendor", vendor);
    dispatch("vendor/fetchVendorPickupTimes", { vendorId }, { root: true });

    if (vendor.hideMenu) {
      commit("setVendorMenu", []);
      return Promise.all([
        dispatch("vendor/fetchSubvendorData", { vendorId, venueId }, { root: true }),
        dispatch("dayParts/setVenueDayParts", null, { root: true }),
        dispatch("cart/reviewShoppingCart", null, { root: true }),
        dispatch("vendor/setAllItemsAttributes", null, { root: true })
      ]).then(() => []);
    }

    return dispatch("updateActiveVendorMenu", vendorId);
  },
  async updateActiveVendorMenu({ commit, dispatch, rootGetters, getters }, vendorId) {
    const venueId = rootGetters["venue/getActiveVenue"];

    const {
      data: { menu }
    } = await apiService.fetchIWVendorMenu(venueId, vendorId);

    //TODO: evaluate moving the sort function to the node layer
    const sortedMenu = sortPosCategories(
      menu.map((posCategory) => ({
        ...posCategory,
        items: sortItems(posCategory.items),
        posSubCategories: sortPosCategories(posCategory.posSubCategories).map((posSubCategory) => ({
          ...posSubCategory,
          items: sortItems(posSubCategory.items)
        }))
      }))
    );

    commit("setVendorMenu", sortedMenu);
    dispatch("common/updateItemsTimeStamp", null, { root: true });

    await Promise.all([
      dispatch("vendor/fetchSubvendorData", { vendorId, venueId }, { root: true }),
      dispatch("dayParts/setVenueDayParts", null, { root: true }),
      dispatch("cart/reviewShoppingCart", null, { root: true }),
      dispatch("vendor/setAllItemsAttributes", null, { root: true })
    ]);

    return menu;
  },
  async fetchVendorItemByExternalId({ getters, rootGetters }, barcode) {
    const venueId = rootGetters["venue/getActiveVenue"];
    const activeVendorId = getters.getActiveVendor.id;
    const { data: item } = await apiService.fetchIWVendorMenuItemByExternalId(venueId, activeVendorId, barcode);
    return item;
  },
  async fetchVendorItemById({ getters, rootGetters }, id) {
    const venueId = rootGetters["venue/getActiveVenue"];
    const activeVendorId = getters.getActiveVendor.id;
    const { data: item } = await apiService.fetchIWVendorMenuItemById(venueId, activeVendorId, id);
    return item;
  },
  async fetchVendorItemsByIds({ getters, rootGetters }, ids) {
    const venueId = rootGetters["venue/getActiveVenue"];
    const activeVendorId = getters.getActiveVendor.id;
    const { data: item } = await apiService.fetchIWVendorMenuItemsByIds(venueId, activeVendorId, ids);
    return item;
  },
  setModifiers({ commit }, mappedModifiers) {
    commit("setModifiers", mappedModifiers);
  },
  /**
   * Grabs disabled/unavailable items by comparing current cart to current menu items, if an item is no longer in menu items, or
   * it's status is no longer ACTIVE, or if it's alcohol and the alcohol enabled flag has changed. This will remove those
   * items from the current cart and show a modal displaying the items that were removed.
   * @param {Object} rootGetters
   * @param {Function} dispatch
   * @param {Function} commit
   * @param {String} routeTo: optional value for navigating on modal close
   */
  async showDisabledItemsModal({ commit, rootGetters, dispatch }, routeTo = "") {
    const isAlcoholEnabled = rootGetters["vendor/isAlcoholEnabled"];
    const cartItems = rootGetters["cart/getCartItems"];
    const disabledCartItems = rootGetters["cart/getDisabledCartItems"](cartItems);
    const disabledAlcoholItems = !isAlcoholEnabled ? rootGetters["cart/getAlcoholItemsFromCart"](cartItems) : [];
    const resultItems = [].concat(disabledCartItems, disabledAlcoholItems);
    const disabledItems = removeDuplicates(resultItems, (item) => item.id);
    commit("cart/clearDisabledCartItems", null, { root: true });

    if (disabledItems.length) {
      // remove disabled or no longer available items from cart
      dispatch("cart/removeDisabledItemsFromCart", { disabledItems }, { root: true });

      dispatch(
        "modals/showModal",
        {
          modalComp: DisabledItemsModal,
          props: {
            routeTo,
            disabledItems,
            showAlcoholMessage: disabledAlcoholItems.length > 0,
            showItemsMessage: disabledCartItems.length > 0
          },
          params: {
            name: "DisabledItemsModal",
            height: "auto",
            width: 500,
            adaptive: true,
            scrollable: true,
            clickToClose: true,
            focusTrap: true
          }
        },
        { root: true }
      );
    }
  }
};

// mutations
const mutations = {
  setVendorMenu(state, menu) {
    state.vendorMenu = menu;
    state.cachedDateTime = new Date();
  },
  setModifier(state, modifier) {
    state.modifiers = { ...state.modifiers, ...modifier };
  },
  resetMenuItemsState(state) {
    Object.assign(state, getDefaultState());
  },
  setModifiers(state, modifiers) {
    state.modifiers = modifiers;
  }
};

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations
};
